import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  Divider,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DnreIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Business as EntityIcon,
  CalendarMonth as CalendarIcon,
  Settings as SettingsIcon,
  VpnKey as KeyIcon,
  CloudUpload as UploadIcon,
  Security as SecurityIcon,
  Receipt as ReceiptIcon,
  Sync as SyncIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

// ================== INTERFACES ==================

interface EFaturaCredentials {
  nif: string;
  username: string;
  password: string;
  certificateName: string;
  certificateExpiry: string;
  environment: 'production' | 'test';
  autoSubmit: boolean;
  lastSync: string;
  status: 'connected' | 'disconnected' | 'error';
}

interface DocumentSeries {
  id: string;
  type: 'FT' | 'NC' | 'ND' | 'FR' | 'RC';
  typeName: string;
  series: string;
  currentNumber: number;
  startNumber: number;
  endNumber: number;
  year: number;
  status: 'active' | 'inactive' | 'exhausted';
  atcud: string;
  validationCode: string;
}

interface TaxCode {
  id: string;
  code: string;
  description: string;
  rate: number;
  type: 'IVA' | 'IS' | 'IUR' | 'ISENTO';
  exemptionReason?: string;
  status: 'active' | 'inactive';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// ================== MOCK DATA ==================

const mockCredentials: EFaturaCredentials = {
  nif: '123456789',
  username: 'pagali_api',
  password: '********',
  certificateName: 'Pagali_DNRE_2024.pfx',
  certificateExpiry: '2025-12-31',
  environment: 'test',
  autoSubmit: true,
  lastSync: '2024-01-10T14:30:00',
  status: 'connected',
};

const mockSeries: DocumentSeries[] = [
  {
    id: '1',
    type: 'FT',
    typeName: 'Fatura',
    series: 'FT2024',
    currentNumber: 1542,
    startNumber: 1,
    endNumber: 999999,
    year: 2024,
    status: 'active',
    atcud: 'ABCD1234-1542',
    validationCode: 'FT24CV001',
  },
  {
    id: '2',
    type: 'NC',
    typeName: 'Nota de Credito',
    series: 'NC2024',
    currentNumber: 87,
    startNumber: 1,
    endNumber: 999999,
    year: 2024,
    status: 'active',
    atcud: 'EFGH5678-87',
    validationCode: 'NC24CV001',
  },
  {
    id: '3',
    type: 'FR',
    typeName: 'Fatura-Recibo',
    series: 'FR2024',
    currentNumber: 423,
    startNumber: 1,
    endNumber: 999999,
    year: 2024,
    status: 'active',
    atcud: 'IJKL9012-423',
    validationCode: 'FR24CV001',
  },
  {
    id: '4',
    type: 'RC',
    typeName: 'Recibo',
    series: 'RC2024',
    currentNumber: 892,
    startNumber: 1,
    endNumber: 999999,
    year: 2024,
    status: 'active',
    atcud: 'MNOP3456-892',
    validationCode: 'RC24CV001',
  },
];

const mockTaxCodes: TaxCode[] = [
  {
    id: '1',
    code: 'NOR',
    description: 'Taxa Normal',
    rate: 15,
    type: 'IVA',
    status: 'active',
  },
  {
    id: '2',
    code: 'RED',
    description: 'Taxa Reduzida',
    rate: 10,
    type: 'IVA',
    status: 'active',
  },
  {
    id: '3',
    code: 'ISE',
    description: 'Isento',
    rate: 0,
    type: 'ISENTO',
    exemptionReason: 'M01 - Artigo 9.o do CIVA',
    status: 'active',
  },
  {
    id: '4',
    code: 'OUT',
    description: 'Outros (Nao sujeito)',
    rate: 0,
    type: 'ISENTO',
    exemptionReason: 'M99 - Nao sujeito ou nao tributado',
    status: 'active',
  },
];

// ================== COMPONENT ==================

const DnreTab: React.FC = () => {
  const [innerTab, setInnerTab] = useState(0);

  // Credentials state
  const [credentials, setCredentials] = useState<EFaturaCredentials>(mockCredentials);
  const [editCredentials, setEditCredentials] = useState(false);

  // Series state
  const [series, setSeries] = useState<DocumentSeries[]>(mockSeries);
  const [seriesDialog, setSeriesDialog] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<DocumentSeries | null>(null);
  const [seriesPage, setSeriesPage] = useState(0);
  const [seriesRowsPerPage, setSeriesRowsPerPage] = useState(10);

  // Tax codes state
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>(mockTaxCodes);
  const [taxDialog, setTaxDialog] = useState(false);
  const [selectedTax, setSelectedTax] = useState<TaxCode | null>(null);

  // Form states
  const [seriesForm, setSeriesForm] = useState({
    type: 'FT' as DocumentSeries['type'],
    series: '',
    startNumber: 1,
    endNumber: 999999,
    year: new Date().getFullYear(),
  });

  const [taxForm, setTaxForm] = useState({
    code: '',
    description: '',
    rate: 15,
    type: 'IVA' as TaxCode['type'],
    exemptionReason: '',
  });

  // ================== HANDLERS ==================

  const handleTestConnection = () => {
    alert('A testar ligacao com o servidor DNRE eFatura...');
    // Simulate API call
    setTimeout(() => {
      setCredentials({ ...credentials, status: 'connected', lastSync: new Date().toISOString() });
    }, 1500);
  };

  const handleSyncNow = () => {
    alert('A sincronizar documentos pendentes com DNRE...');
  };

  const getDocTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
      FT: 'primary',
      NC: 'error',
      ND: 'warning',
      FR: 'success',
      RC: 'secondary',
    };
    return colors[type] || 'primary';
  };

  const getStatusChip = (status: string) => {
    const configs: Record<string, { color: 'success' | 'error' | 'warning' | 'default'; label: string }> = {
      active: { color: 'success', label: 'Activo' },
      inactive: { color: 'default', label: 'Inactivo' },
      exhausted: { color: 'warning', label: 'Esgotado' },
      connected: { color: 'success', label: 'Conectado' },
      disconnected: { color: 'default', label: 'Desconectado' },
      error: { color: 'error', label: 'Erro' },
    };
    const config = configs[status] || configs.inactive;
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  const handleOpenSeriesDialog = (s?: DocumentSeries) => {
    if (s) {
      setSelectedSeries(s);
      setSeriesForm({
        type: s.type,
        series: s.series,
        startNumber: s.startNumber,
        endNumber: s.endNumber,
        year: s.year,
      });
    } else {
      setSelectedSeries(null);
      setSeriesForm({
        type: 'FT',
        series: '',
        startNumber: 1,
        endNumber: 999999,
        year: new Date().getFullYear(),
      });
    }
    setSeriesDialog(true);
  };

  const handleSaveSeries = () => {
    if (selectedSeries) {
      setSeries(series.map(s => s.id === selectedSeries.id ? { ...s, ...seriesForm } : s));
    } else {
      const typeNames: Record<string, string> = {
        FT: 'Fatura',
        NC: 'Nota de Credito',
        ND: 'Nota de Debito',
        FR: 'Fatura-Recibo',
        RC: 'Recibo',
      };
      const newSeries: DocumentSeries = {
        id: crypto.randomUUID(),
        ...seriesForm,
        typeName: typeNames[seriesForm.type],
        currentNumber: seriesForm.startNumber,
        status: 'active',
        atcud: `${seriesForm.series}-${seriesForm.startNumber}`,
        validationCode: `${seriesForm.type}${seriesForm.year.toString().slice(-2)}CV${String(series.length + 1).padStart(3, '0')}`,
      };
      setSeries([...series, newSeries]);
    }
    setSeriesDialog(false);
  };

  const handleOpenTaxDialog = (t?: TaxCode) => {
    if (t) {
      setSelectedTax(t);
      setTaxForm({
        code: t.code,
        description: t.description,
        rate: t.rate,
        type: t.type,
        exemptionReason: t.exemptionReason || '',
      });
    } else {
      setSelectedTax(null);
      setTaxForm({
        code: '',
        description: '',
        rate: 15,
        type: 'IVA',
        exemptionReason: '',
      });
    }
    setTaxDialog(true);
  };

  const handleSaveTax = () => {
    if (selectedTax) {
      setTaxCodes(taxCodes.map(t => t.id === selectedTax.id ? { ...t, ...taxForm, status: 'active' } : t));
    } else {
      const newTax: TaxCode = {
        id: crypto.randomUUID(),
        ...taxForm,
        status: 'active',
      };
      setTaxCodes([...taxCodes, newTax]);
    }
    setTaxDialog(false);
  };

  const handleDeleteSeries = (s: DocumentSeries) => {
    if (window.confirm(`Eliminar serie ${s.series}?`)) {
      setSeries(series.filter(item => item.id !== s.id));
    }
  };

  const handleDeleteTax = (t: TaxCode) => {
    if (window.confirm(`Eliminar codigo ${t.code}?`)) {
      setTaxCodes(taxCodes.filter(item => item.id !== t.id));
    }
  };

  // ================== RENDER ==================

  return (
    <Box>
      {/* Header with info */}
      <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          Configuracao para integracao com o sistema <strong>eFatura</strong> da DNRE - Direccao Nacional de Receitas do Estado de Cabo Verde.{' '}
          <Link href="https://efatura.gov.cv" target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            Portal eFatura <OpenInNewIcon fontSize="inherit" />
          </Link>
        </Typography>
      </Alert>

      {/* Inner Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={innerTab}
          onChange={(_, v) => setInnerTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Credenciais" />
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Series Documentos" />
          <Tab icon={<DnreIcon />} iconPosition="start" label="Codigos Imposto" />
        </Tabs>
      </Paper>

      {/* TAB 0: Credenciais */}
      <TabPanel value={innerTab} index={0}>
        <Grid container spacing={3}>
          {/* Connection Status Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SecurityIcon color="primary" />
                  <Typography variant="h6">Estado da Ligacao</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {getStatusChip(credentials.status)}
                  <Typography variant="body2" color="text.secondary">
                    Ambiente: {credentials.environment === 'production' ? 'Producao' : 'Teste'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Ultima sincronizacao: {new Date(credentials.lastSync).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={handleTestConnection}>
                    Testar
                  </Button>
                  <Button size="small" variant="contained" startIcon={<SyncIcon />} onClick={handleSyncNow}>
                    Sincronizar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Credentials Card */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <KeyIcon color="primary" />
                    <Typography variant="h6">Credenciais eFatura</Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setEditCredentials(!editCredentials)}
                  >
                    {editCredentials ? 'Cancelar' : 'Editar'}
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="NIF da Entidade"
                      value={credentials.nif}
                      disabled={!editCredentials}
                      onChange={(e) => setCredentials({ ...credentials, nif: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Username API"
                      value={credentials.username}
                      disabled={!editCredentials}
                      onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Password API"
                      type="password"
                      value={credentials.password}
                      disabled={!editCredentials}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Ambiente"
                      value={credentials.environment}
                      disabled={!editCredentials}
                      onChange={(e) => setCredentials({ ...credentials, environment: e.target.value as 'production' | 'test' })}
                    >
                      <MenuItem value="test">Teste (Sandbox)</MenuItem>
                      <MenuItem value="production">Producao</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={credentials.autoSubmit}
                          disabled={!editCredentials}
                          onChange={(e) => setCredentials({ ...credentials, autoSubmit: e.target.checked })}
                        />
                      }
                      label="Submissao Automatica"
                    />
                  </Grid>
                </Grid>
                {editCredentials && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setEditCredentials(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={() => setEditCredentials(false)}>
                      Guardar
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Certificate Card */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SecurityIcon color="primary" />
                  <Typography variant="h6">Certificado Digital</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" color="text.secondary">Certificado Actual:</Typography>
                    <Typography variant="body1" fontWeight={500}>{credentials.certificateName}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" color="text.secondary">Validade:</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {new Date(credentials.certificateExpiry).toLocaleDateString('pt-PT', { timeZone: 'UTC' })}
                      {new Date(credentials.certificateExpiry) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) && (
                        <Chip size="small" color="warning" label="Expira em breve" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Button variant="outlined" startIcon={<UploadIcon />}>
                      Carregar Novo Certificado
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* TAB 1: Series de Documentos */}
      <TabPanel value={innerTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={500}>
            Series de Documentos Fiscais
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenSeriesDialog()}>
            Nova Serie
          </Button>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          As series de documentos devem ser comunicadas a DNRE antes da sua utilizacao. O codigo ATCUD e gerado automaticamente.
        </Alert>

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={80}>Tipo</TableCell>
                  <TableCell>Serie</TableCell>
                  <TableCell width={120} align="center">Numero Actual</TableCell>
                  <TableCell width={120} align="center">Intervalo</TableCell>
                  <TableCell width={80} align="center">Ano</TableCell>
                  <TableCell width={150}>ATCUD</TableCell>
                  <TableCell width={100}>Estado</TableCell>
                  <TableCell width={100} align="center">Accoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {series.slice(seriesPage * seriesRowsPerPage, seriesPage * seriesRowsPerPage + seriesRowsPerPage).map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Chip size="small" color={getDocTypeColor(s.type)} label={s.type} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{s.series}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.typeName}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="primary">
                        {s.currentNumber.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {s.startNumber.toLocaleString()} - {s.endNumber.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{s.year}</TableCell>
                    <TableCell>
                      <Tooltip title="Codigo Unico do Documento">
                        <Typography variant="body2" fontFamily="monospace" fontSize={11}>
                          {s.atcud}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{getStatusChip(s.status)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenSeriesDialog(s)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteSeries(s)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={series.length}
            page={seriesPage}
            onPageChange={(_, p) => setSeriesPage(p)}
            rowsPerPage={seriesRowsPerPage}
            onRowsPerPageChange={(e) => { setSeriesRowsPerPage(parseInt(e.target.value)); setSeriesPage(0); }}
            rowsPerPageOptions={[10, 25]}
            labelRowsPerPage="Linhas:"
          />
        </Paper>
      </TabPanel>

      {/* TAB 2: Codigos de Imposto */}
      <TabPanel value={innerTab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={500}>
            Codigos de Imposto (IVA Cabo Verde)
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenTaxDialog()}>
            Novo Codigo
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Taxas de IVA em vigor em Cabo Verde: Taxa Normal 15%, Taxa Reduzida 10%, Isento 0%. Codigo do Pais: CV
        </Alert>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {taxCodes.filter(t => t.status === 'active').map((t) => (
            <Grid size={{ xs: 6, md: 3 }} key={t.id}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Chip
                        size="small"
                        color={t.type === 'IVA' ? 'primary' : 'default'}
                        label={t.code}
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="h4" fontWeight={700} color="primary">
                        {t.rate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t.description}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleOpenTaxDialog(t)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={100}>Codigo</TableCell>
                  <TableCell>Descricao</TableCell>
                  <TableCell width={100} align="center">Taxa (%)</TableCell>
                  <TableCell width={100}>Tipo</TableCell>
                  <TableCell>Motivo Isencao</TableCell>
                  <TableCell width={100}>Estado</TableCell>
                  <TableCell width={100} align="center">Accoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taxCodes.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                        {t.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{t.description}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="primary">
                        {t.rate}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={t.type} color={t.type === 'IVA' ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {t.exemptionReason || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(t.status)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenTaxDialog(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteTax(t)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Dialog: Nova/Editar Serie */}
      <Dialog open={seriesDialog} onClose={() => setSeriesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSeries ? 'Editar Serie' : 'Nova Serie de Documentos'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Tipo de Documento"
                value={seriesForm.type}
                onChange={(e) => setSeriesForm({ ...seriesForm, type: e.target.value as DocumentSeries['type'] })}
                disabled={!!selectedSeries}
              >
                <MenuItem value="FT">FT - Fatura</MenuItem>
                <MenuItem value="NC">NC - Nota de Credito</MenuItem>
                <MenuItem value="ND">ND - Nota de Debito</MenuItem>
                <MenuItem value="FR">FR - Fatura-Recibo</MenuItem>
                <MenuItem value="RC">RC - Recibo</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Codigo da Serie"
                value={seriesForm.series}
                onChange={(e) => setSeriesForm({ ...seriesForm, series: e.target.value.toUpperCase() })}
                placeholder="Ex: FT2024"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Numero Inicial"
                value={seriesForm.startNumber}
                onChange={(e) => setSeriesForm({ ...seriesForm, startNumber: parseInt(e.target.value) || 1 })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Numero Final"
                value={seriesForm.endNumber}
                onChange={(e) => setSeriesForm({ ...seriesForm, endNumber: parseInt(e.target.value) || 999999 })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Ano Fiscal"
                value={seriesForm.year}
                onChange={(e) => setSeriesForm({ ...seriesForm, year: parseInt(e.target.value) || new Date().getFullYear() })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeriesDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveSeries} disabled={!seriesForm.series}>
            {selectedSeries ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Novo/Editar Codigo Imposto */}
      <Dialog open={taxDialog} onClose={() => setTaxDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedTax ? 'Editar Codigo de Imposto' : 'Novo Codigo de Imposto'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Codigo"
                value={taxForm.code}
                onChange={(e) => setTaxForm({ ...taxForm, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Tipo"
                value={taxForm.type}
                onChange={(e) => setTaxForm({ ...taxForm, type: e.target.value as TaxCode['type'] })}
              >
                <MenuItem value="IVA">IVA</MenuItem>
                <MenuItem value="IS">Imposto de Selo</MenuItem>
                <MenuItem value="IUR">IUR</MenuItem>
                <MenuItem value="ISENTO">Isento</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Descricao"
                value={taxForm.description}
                onChange={(e) => setTaxForm({ ...taxForm, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Taxa (%)"
                value={taxForm.rate}
                onChange={(e) => setTaxForm({ ...taxForm, rate: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            {(taxForm.type === 'ISENTO' || taxForm.rate === 0) && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Motivo de Isencao"
                  value={taxForm.exemptionReason}
                  onChange={(e) => setTaxForm({ ...taxForm, exemptionReason: e.target.value })}
                  placeholder="Ex: M01 - Artigo 9.o do CIVA"
                  helperText="Obrigatorio para taxas isentas ou 0%"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveTax} disabled={!taxForm.code || !taxForm.description}>
            {selectedTax ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DnreTab;
